<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcRegisterSelector',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'update:modelValue']);

  const props = defineProps({
    ...userComponentProps,
    modelValue: {
      type: Object,
      required: false,
      default: { selectedRegisterId: null, selectedVersionId: null },
    },
    cacheKey: {
      type: String,
      required: false,
    },
    companyId: {
      type: String,
      required: false,
    },
    variant: {
      type: String,
      required: false,
      default: 'light',
    },
    toggleClass: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const applySelection = (...args) => {
    const { selectedRegisterId, selectedVersionId } = props.modelValue ?? {};

    if (args[0]) {
      let [{ register, version }] = args;

      if (!register || !register.versions) {
        return null;
      }

      if (!version) {
        version = _.chain(register.versions.data)
          .orderBy(['financial_year'], ['desc'])
          .first()
          .value();
      }

      if (
        selectedRegisterId != register.id ||
        selectedVersionId != version.id
      ) {
        emit('update:modelValue', {
          selectedRegisterId: register.id,
          selectedVersionId: version.id,
        });
      }
      return args[0];
    }

    emit('update:modelValue', {
      selectedRegisterId: null,
      selectedVersionId: null,
    });

    return null;
  };
  const selectByCache = (...args) => {
    const [registers, value] = args;
    const { selectedRegisterId, selectedVersionId } = value;

    if (selectedRegisterId && selectedVersionId) {
      let selection = null;

      for (let register of registers) {
        if (register.id == selectedRegisterId && register.versions) {
          for (let version of register.versions.data) {
            if (version.id == selectedVersionId) {
              return { register, version };
            }
          }
        }
      }
    }
  };
  const selectFirst = (...args) => {
    const [registers] = args;

    if (registers && registers.length) {
      for (let register of registers) {
        if (register.versions) {
          for (let version of _.orderBy(
            register.versions.data,
            ['financial_year'],
            ['desc']
          )) {
            return { register, version };
          }
        }
      }
    }
  };

  const registers = ref(null);
  const selectedRegister = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    registers: registers.value,
    selectedRegister: selectedRegister.value,
    ux: ux.value,
  }));

  const $$load_registers = () => {
    return $fn.value
      .fetch('list_register', { parameters: { company_id: props.companyId } })
      .then((d) => _.sortBy(d, 'name'));
  };
  const $$load_selectedRegister = () => {
    return applySelection(
      selectByCache($data.value.registers, props.modelValue) ||
        selectFirst($data.value.registers)
    );
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'registers' },
      { name: 'selectedRegister', dependsOn: ['registers'] },
      { name: 'ux' },
    ],
    loaders: {
      registers: $$load_registers,
      selectedRegister: $$load_selectedRegister,
    },
    ux: {},
    dataRefs: { registers, selectedRegister, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const noRegisterDefined = computed(() => {
    if (loaded.value) {
      return !registers.value.length;
    }
    return null;
  });
  const dropdownToggleClass = computed(() => {
    return 'border ' + (props.toggleClass || '');
  });

  watch(
    () => props.modelValue,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modelValue');
    }
  );
  watch(
    () => props.cacheKey,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('cacheKey');
    }
  );
  watch(
    () => props.companyId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('companyId');
    }
  );
  watch(
    () => props.variant,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('variant');
    }
  );
  watch(
    () => props.toggleClass,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('toggleClass');
    }
  );

  const act_5962862420439144_setData_33end986d_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedRegister',
      value: applySelection({ register: $event.scope.register }),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_1048678296148443_showModal_pdxsqugkp_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Registers',
    };
  };
  const act_5974459770896956_setData_653fstztl_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedRegister',
      value: applySelection({
        register: selectedRegister.value.register,
        version: $event.scope.version,
      }),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };

  const act_5962862420439144_setData_33end986d = new actionTypes.SetDataAction({
    actionArgs: act_5962862420439144_setData_33end986d_getActionArgs,
  });
  const inst_evh_5962862420439144_clickPrevent_efemf6hv3 =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_setData_33end986d,
      event: 'click.prevent',
      displayName: 'setData',
    });
  const inst_evh_5962862420439144_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_clickPrevent_efemf6hv3],
      event: 'click.prevent',
    });
  const act_1048678296148443_reloadComponentData_6is4z8x5i =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_1048678296148443_close_fmd8pnas7 =
    new actionTypes.EventHandlerImpl({
      action: act_1048678296148443_reloadComponentData_6is4z8x5i,
      event: 'close',
      displayName: 'reloadComponentData',
    });
  const act_1048678296148443_showModal_pdxsqugkp =
    new actionTypes.ShowModalAction({
      actionArgs: act_1048678296148443_showModal_pdxsqugkp_getActionArgs,
      events: [inst_evh_1048678296148443_close_fmd8pnas7],
    });
  const inst_evh_1048678296148443_clickPrevent_ox033h59m =
    new actionTypes.EventHandlerImpl({
      action: act_1048678296148443_showModal_pdxsqugkp,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_1048678296148443_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1048678296148443_clickPrevent_ox033h59m],
      event: 'click.prevent',
    });
  const act_5974459770896956_setData_653fstztl = new actionTypes.SetDataAction({
    actionArgs: act_5974459770896956_setData_653fstztl_getActionArgs,
  });
  const inst_evh_5974459770896956_clickPrevent_454n9afsd =
    new actionTypes.EventHandlerImpl({
      action: act_5974459770896956_setData_653fstztl,
      event: 'click.prevent',
      displayName: 'setData',
    });
  const inst_evh_5974459770896956_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5974459770896956_clickPrevent_454n9afsd],
      event: 'click.prevent',
    });

  const actions = {
    inst_evh_5962862420439144_clickPrevent_efemf6hv3,
    inst_evh_5962862420439144_clickPrevent,
    inst_evh_1048678296148443_close_fmd8pnas7,
    inst_evh_1048678296148443_clickPrevent_ox033h59m,
    inst_evh_1048678296148443_clickPrevent,
    inst_evh_5974459770896956_clickPrevent_454n9afsd,
    inst_evh_5974459770896956_clickPrevent,
  };

  const evh_5962862420439144_clickPrevent_efemf6hv3 = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_clickPrevent_efemf6hv3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1048678296148443_close_fmd8pnas7 = async (event, scope) => {
    return await actions.inst_evh_1048678296148443_close_fmd8pnas7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1048678296148443_clickPrevent_ox033h59m = async (event, scope) => {
    return await actions.inst_evh_1048678296148443_clickPrevent_ox033h59m.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1048678296148443_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_1048678296148443_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5974459770896956_clickPrevent_454n9afsd = async (event, scope) => {
    return await actions.inst_evh_5974459770896956_clickPrevent_454n9afsd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5974459770896956_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_5974459770896956_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <BDropdown
    :toggle-class="dropdownToggleClass"
    :variant="variant"
    strategy="fixed"
    class="apply-inline-button"
    v-if="loaded"
  >
    <template #button-content>
      <ItpText
        :text="
          pgettext('RegisterSelector', 'No register have been defined yet.')
        "
        v-if="noRegisterDefined"
      >
      </ItpText>
      <ItpText
        :text="pgettext('RegisterSelector', 'Select register')"
        v-if="!noRegisterDefined && !selectedRegister"
      >
      </ItpText>
      <div v-if="selectedRegister && selectedRegister.register">
        <ItpText
          :muted="selectedRegister.register.inactive"
          :strikethrough="selectedRegister.register.inactive"
        >
          <span class="pe-1">
            {{ selectedRegister.register.name }}
          </span>
        </ItpText>
      </div>
    </template>
    <BDropdownItem
      :key="register.id"
      v-for="register in registers"
      @click.prevent="evh_5962862420439144_clickPrevent($event, { register })"
    >
      <ItpText :muted="register.inactive" :strikethrough="register.inactive">
        <strong>
          {{ register.name }}
        </strong>
      </ItpText>
    </BDropdownItem>
    <BDropdownDivider v-if="!noRegisterDefined"> </BDropdownDivider>
    <ItpNavItem
      href="#"
      dropdown-item
      icon="fa-cog"
      :text="pgettext('RegisterSelector', 'Configure registers...')"
      @click.prevent="evh_1048678296148443_clickPrevent($event, {})"
    >
    </ItpNavItem>
  </BDropdown>
  <BDropdown
    :toggle-class="dropdownToggleClass"
    variant="light"
    strategy="fixed"
    class="apply-inline-button mx-2"
    v-if="
      loaded &&
      !noRegisterDefined &&
      selectedRegister &&
      selectedRegister.register
    "
  >
    <template #button-content>
      <ItpText
        :muted="selectedRegister.version && selectedRegister.version.inactive"
        :strikethrough="
          selectedRegister.version && selectedRegister.version.inactive
        "
      >
        <span class="pe-1">
          {{ selectedRegister.version && selectedRegister.version.name }}
        </span>
      </ItpText>
    </template>
    <BDropdownItem
      :key="version.id"
      v-for="version in selectedRegister.register.versions.data"
      @click.prevent="evh_5974459770896956_clickPrevent($event, { version })"
    >
      <ItpText :muted="version.inactive" :strikethrough="version.inactive">
        <strong>
          {{ version.name }}
        </strong>
      </ItpText>
    </BDropdownItem>
  </BDropdown>
</template>
